import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { useUser } from '@/components/auth-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

import { GoogleIcon } from './google-icon';
import { getFirebaseMessage } from './helpers';
import { signUpSchema, type SignUpValues } from './schema';

interface Props {
  onSignIn: () => void;
}

export function SignUpForm({ onSignIn }: Props) {
  const { signUp, logInWithGoogle } = useUser();
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignUpValues>({ resolver: zodResolver(signUpSchema) });

  async function onSubmit({ email, password }: SignUpValues) {
    const error = await signUp(email, password);
    if (error) toast.error(getFirebaseMessage(error));
  }

  async function handleGoogle() {
    setGoogleLoading(true);
    const error = await logInWithGoogle(getValues('email'));
    if (error) toast.error(getFirebaseMessage(error));
    setGoogleLoading(false);
  }

  return (
    <Card className="w-full max-w-sm gap-4 py-8">
      <CardHeader>
        <CardTitle className="text-center text-xl">Create an account</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="su-email">Email</Label>
            <Input
              id="su-email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              aria-invalid={Boolean(errors.email)}
              data-testid="email-input"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-destructive text-xs">{errors.email.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="su-password">Password</Label>
            <Input
              id="su-password"
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              aria-invalid={Boolean(errors.password)}
              data-testid="password-input"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-destructive text-xs">{errors.password.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="su-confirm">Confirm password</Label>
            <Input
              id="su-confirm"
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              aria-invalid={Boolean(errors.confirmPassword)}
              data-testid="confirm-password-input"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-destructive text-xs">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="mt-1 w-full"
            data-loading={isSubmitting}
            data-testid="sign-up-button"
          >
            {isSubmitting && <Loader2Icon className="animate-spin" />}
            Create account
          </Button>
        </form>

        <div className="flex items-center gap-2">
          <Separator className="flex-1" />
          <span className="text-muted-foreground text-xs">or</span>
          <Separator className="flex-1" />
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={handleGoogle}
          data-loading={googleLoading}
          data-testid="google-button"
          type="button"
        >
          {googleLoading ? (
            <Loader2Icon className="animate-spin" />
          ) : (
            <GoogleIcon />
          )}
          Continue with Google
        </Button>

        <p className="text-muted-foreground text-center text-sm">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSignIn}
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            Sign in
          </button>
        </p>
      </CardContent>
    </Card>
  );
}
